import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import {
  FormAnswerDTO,
  ReportFiltersDTO,
} from 'src/app/private/pages/reporting-page/filters/report-filters.dto';
import { TopicDTO } from 'src/app/private/shared/components/topic-select/topic-select.component';
import { CoachingSessionTypeDTO } from 'src/app/private/shared/dtos/coaching-session.dto';
import { CompetencyDTO } from 'src/app/private/shared/dtos/competencies.dto';
import { PdEvent } from 'src/app/private/shared/dtos/pd.dto';
import { FiltersetValueItem } from 'src/app/private/shared/dtos/report-filterset.dto';
import { DWCohort } from 'src/app/private/shared/types/responses/cohort.responses';
import { ProFormsAPIResponse } from 'src/app/private/shared/types/responses/proforms.responses';
import { UserDTO, UserLiteDTO } from '../../state/user/user.dto';
import { User } from '../../state/user/user.model';
import { dateTimeFromNgbDate } from '../../utilities/date-helpers';
import { booleanNumberFromBoolean } from '../../utilities/enums/boolean-number.enum';
import { DistrictDTO, DistrictSimpleDto } from '../district.dto';
import { KpiReportFilter } from '../reports.dto';
import { SchoolDTO, SchoolYearDTO } from '../school.dto';

export interface ReportFiltersReverseContext {
  schoolYears: SchoolYearDTO[];
  district: DistrictSimpleDto;
  multiSchoolYear?: boolean;
}

export const translateKpiReportFilterFromFilterDTO = (
  filterDTO: ReportFiltersDTO
): KpiReportFilter[] => {
  const returnArray: KpiReportFilter[] = [];
  Object.keys(filterDTO).forEach((key) => {
    /* eslint-disable-next-line default-case */
    switch (key) {
      case 'dateRangeFilter':
        returnArray.push({
          codename: 'daterange',
          title: 'Date Range',
          value: [
            dateTimeFromNgbDate(
              (filterDTO.dateRangeFilter as Array<NgbDate>)[0]
            ).toFormat('yyyy-MM-dd'),
            dateTimeFromNgbDate(
              (filterDTO.dateRangeFilter as Array<NgbDate>)[1]
            ).toFormat('yyyy-MM-dd'),
          ],
        });
        break;
      case 'logTypeFilter':
        returnArray.push({
          codename: 'session_type',
          title: 'Session Type',
          value: (filterDTO.logTypeFilter as CoachingSessionTypeDTO[]).map(
            (logType) => logType.id.toString()
          ),
        });
        break;
      case 'schoolYearFilter':
        if (filterDTO.schoolYearFilter) {
          returnArray.push({
            codename: 'school_year',
            title: 'School Year',
            value: [filterDTO.schoolYearFilter?.id.toString()],
            displayName: filterDTO.schoolYearFilter?.name,
          });
        }

        break;
      case 'schoolYearsFilter':
        returnArray.push({
          codename: 'school_year',
          title: 'School Year',
          value: (filterDTO.schoolYearsFilter as SchoolYearDTO[]).map((year) =>
            year.id.toString()
          ),
          displayName: (filterDTO.schoolYearsFilter as SchoolYearDTO[])
            .map((year) => year.name)
            .join(','),
        });
        break;
      case 'districtFilter':
        returnArray.push({
          codename: 'district',
          title: 'District',
          value: (filterDTO.districtFilter as DistrictDTO[]).map((district) =>
            district.id.toString()
          ),
          displayName: filterDTO.districtFilter
            ?.map((district) => district.title)
            .join(','),
        });
        break;
      case 'schoolFilter':
        if (filterDTO.schoolFilter) {
          returnArray.push({
            codename: 'school',
            title: 'School',
            value: (filterDTO.schoolFilter as SchoolDTO[]).map((school) =>
              school.id.toString()
            ),
            displayName: filterDTO.schoolFilter
              ?.map((school) => school.title)
              .join(','),
          });
        }
        break;
      case 'cohortFilter':
        returnArray.push({
          codename: 'cohort',
          title: 'Cohort',
          value: (filterDTO.cohortFilter as DWCohort[]).map((cohort) =>
            cohort.id.toString()
          ),
          displayName: (filterDTO.cohortFilter as DWCohort[])
            .map((cohort) => cohort.name)
            .join(','),
        });
        break;
      case 'assignedCoachFilter':
        returnArray.push({
          codename: 'assigned_coach',
          title: 'Assigned Coach',
          value: [(filterDTO.assignedCoachFilter as User).id.toString()],
          displayName: (filterDTO.assignedCoachFilter as User).fullName,
        });
        break;
      case 'coacheesFilter':
        returnArray.push({
          codename: 'user',
          title: 'Coachee',
          value: (filterDTO.coacheesFilter as UserDTO[]).map((coachee) =>
            coachee.id.toString()
          ),
          displayName: filterDTO.coacheesFilter
            ?.map(
              (coachee) =>
                `${coachee.profile.first_name} ${coachee.profile.last_name}`
            )
            .join(', '),
        });
        break;

      case 'planUserFilter':
        returnArray.push({
          codename: 'user',
          title: 'Plan User',
          value: (filterDTO.planUserFilter as UserDTO[]).map((planUser) =>
            planUser.id.toString()
          ),
          displayName: filterDTO.planUserFilter
            ?.map(
              (planUser) =>
                `${planUser.profile.first_name} ${planUser.profile.last_name}`
            )
            .join(', '),
        });
        break;

      case 'participants':
        returnArray.push({
          codename: 'user',
          title: 'Participant',
          value: (filterDTO.participants as UserDTO[]).map((participant) =>
            participant.id.toString()
          ),
          displayName: filterDTO.participants
            ?.map(
              (participant) =>
                `${participant.profile.first_name} ${participant.profile.last_name}`
            )
            .join(', '),
        });
        break;

      case 'eventFilter':
        if (filterDTO.eventFilter) {
          returnArray.push({
            codename: 'pd_event',
            title: 'PD Event',
            value: (filterDTO.eventFilter as PdEvent[]).map((event) =>
              event.id.toString()
            ),
            displayName: filterDTO.eventFilter
              ?.map((event) => event.title)
              .join(','),
          });
        }
        break;

      case 'facilitators':
        if (filterDTO.facilitators) {
          returnArray.push({
            codename: 'pd_facilitator',
            title: 'PD Faciliator',
            value: (filterDTO.facilitators as UserLiteDTO[]).map(
              (facilitator) => facilitator.id.toString()
            ),
            displayName: filterDTO.facilitators
              ?.map((facilitator) => facilitator.email)
              .join(','),
          });
        }
        break;

      case 'sessionOwners':
        if (filterDTO.sessionOwners) {
          returnArray.push({
            codename: 'session_owner',
            title: 'Session Owner',
            value: (filterDTO.sessionOwners as UserLiteDTO[]).map(
              (session_owner) => session_owner.id.toString()
            ),
            displayName: filterDTO.sessionOwners
              ?.map((session_owner) => session_owner.email)
              .join(','),
          });
        }
        break;

      case 'keyword':
        if (filterDTO.keyword) {
          returnArray.push({
            codename: 'keyword',
            title: 'Keyword',
            value: [filterDTO.keyword ? filterDTO.keyword : ''],
            displayName: filterDTO.keyword ? filterDTO.keyword : '',
          });
        }
        break;

      case 'virtualFilter':
        if (filterDTO.virtualFilter !== undefined) {
          returnArray.push({
            codename: 'is_virtual',
            title: 'Is Virtual',
            value: [filterDTO.virtualFilter.toString()],
            displayName:
              filterDTO.virtualFilter.toString() === '1' ? 'Yes' : 'No',
          });
        }
        break;

      case 'completedFilter':
        if (filterDTO.completedFilter !== undefined) {
          returnArray.push({
            codename: 'pd_session_completed',
            title: 'PD Session Completed',
            value: [filterDTO.completedFilter.toString()],
            displayName:
              filterDTO.completedFilter.toString() === '1' ? 'Yes' : 'No',
          });
        }
        break;

      case 'typesFilter':
        returnArray.push({
          codename: 'pd_session_type',
          title: 'PD Session Type',
          value: filterDTO.typesFilter ? filterDTO.typesFilter : [],
          displayName: filterDTO.typesFilter ? filterDTO.typesFilter[0] : '',
        });
        break;
      case 'categories':
        if (filterDTO.categories && filterDTO.categories.length > 0) {
          returnArray.push({
            codename: 'category',
            title: 'Category',
            value: filterDTO.categories ? filterDTO.categories : [],
            displayName: filterDTO.categories ? filterDTO.categories[0] : '',
          });
        }
        break;
      case 'category_options':
        if (
          filterDTO.category_options &&
          filterDTO.category_options.length > 0
        ) {
          returnArray.push({
            codename: 'category_option',
            title: 'Category Option',
            value: filterDTO.category_options ? filterDTO.category_options : [],
            displayName: filterDTO.category_options
              ? filterDTO.category_options[0]
              : '',
          });
        }
        break;
      case 'competencyList':
        returnArray.push({
          codename: 'competency',
          title: 'Competency',
          value: (filterDTO.competencyList as CompetencyDTO[]).map(
            (competency: CompetencyDTO) => competency.id.toString()
          ),
          displayName: (filterDTO.competencyList as CompetencyDTO[])
            .map((competency: CompetencyDTO) => competency.title)
            .join(', '),
        });
        break;
      case 'proFormFilter':
        returnArray.push({
          codename: 'form',
          title: 'Form',
          value: [
            (filterDTO.proFormFilter as ProFormsAPIResponse).id.toString(),
          ],
          displayName: (filterDTO.proFormFilter as ProFormsAPIResponse).title,
        });
        break;
      case 'formAnswersFilter':
        returnArray.push({
          codename: 'question_answer_submitted',
          value: (filterDTO.formAnswersFilter as FormAnswerDTO[]).map(
            (answer) => answer.id.toString()
          ),
        });
        break;
      case 'topicFilter':
        returnArray.push({
          codename: 'rubric',
          value: [(filterDTO.topicFilter as TopicDTO).id.toString()],
        });
        break;
      case 'hasOption':
        returnArray.push({
          codename: 'has_option',
          value: [
            booleanNumberFromBoolean(filterDTO.hasOption as boolean).toString(),
          ],
        });
        break;
      case 'isReviewed':
        returnArray.push({
          codename: 'is_reviewed',
          value: [
            booleanNumberFromBoolean(
              filterDTO.isReviewed as boolean
            ).toString(),
          ],
        });
        break;
    }
  });
  return returnArray;
};

export const translateReportFiltersDTOFromKpiFilters = (
  kpiFilters: KpiReportFilter[],
  context: ReportFiltersReverseContext
): ReportFiltersDTO => {
  const dto: ReportFiltersDTO = {
    districtFilter: [context.district],
  };

  kpiFilters.forEach((filter) => {
    const values = filter.value as string[];
    switch (filter.codename) {
      case 'daterange':
        if (values.length >= 2) {
          const parseDate = (s: string): NgbDate => {
            const [y, m, d] = s.split('-').map(Number);
            return new NgbDate(y, m, d);
          };
          dto.dateRangeFilter = [parseDate(values[0]), parseDate(values[1])];
        }
        break;
      case 'school_year':
        if (context.multiSchoolYear) {
          dto.schoolYearsFilter = values
            .map((v) => context.schoolYears.find((sy) => sy.id === Number(v)))
            .filter((sy): sy is SchoolYearDTO => sy !== undefined);
        } else {
          dto.schoolYearFilter = context.schoolYears.find(
            (sy) => sy.id === Number(values[0])
          );
        }
        break;
      case 'district':
        dto.districtFilter = values.map((v) => ({
          ...context.district,
          id: Number(v),
        }));
        break;
      case 'school':
        dto.schoolFilter = values.map((v) => ({ id: Number(v) } as SchoolDTO));
        break;
      case 'cohort':
        dto.cohortFilter = values.map((v) => ({ id: Number(v) } as DWCohort));
        break;
      case 'session_type':
        dto.logTypeFilter = values.map(
          (v) => ({ id: Number(v) } as CoachingSessionTypeDTO)
        );
        break;
      case 'assigned_coach':
        dto.assignedCoachFilter = { id: Number(values[0]) } as UserLiteDTO;
        break;
      case 'user':
        dto.coacheesFilter = values.map(
          (v) => ({ id: Number(v) } as UserLiteDTO)
        );
        break;
      case 'pd_event':
        dto.eventFilter = values.map((v) => ({ id: Number(v) } as PdEvent));
        break;
      case 'pd_facilitator':
        dto.facilitators = values.map(
          (v) => ({ id: Number(v) } as UserLiteDTO)
        );
        break;
      case 'session_owner':
        dto.sessionOwners = values.map(
          (v) => ({ id: Number(v) } as UserLiteDTO)
        );
        break;
      case 'keyword':
        dto.keyword = values[0];
        break;
      case 'is_virtual':
        dto.virtualFilter = Number(values[0]);
        break;
      case 'pd_session_completed':
        dto.completedFilter = Number(values[0]);
        break;
      case 'pd_session_type':
        dto.typesFilter = values;
        break;
      case 'category':
        dto.categories = values;
        break;
      case 'category_option':
        dto.category_options = values;
        break;
      case 'competency':
        dto.competencyList = values.map(
          (v) => ({ id: Number(v) } as CompetencyDTO)
        );
        break;
      case 'form':
        dto.proFormFilter = { id: Number(values[0]) } as ProFormsAPIResponse;
        break;
      case 'rubric':
        dto.topicFilter = { id: Number(values[0]) } as TopicDTO;
        break;
      case 'has_option':
        dto.hasOption = values[0] === '1';
        break;
      case 'is_reviewed':
        dto.isReviewed = values[0] === '1';
        break;
      default:
        break;
    }
  });

  return dto;
};

const userName = (u: UserLiteDTO): string =>
  u.profile
    ? `${u.profile.first_name} ${u.profile.last_name}`.trim()
    : u.email ?? '';

const push = (
  result: { filter_name: string; filter_value: string }[],
  name: string,
  items: FiltersetValueItem[]
) => {
  if (items.length) {
    result.push({ filter_name: name, filter_value: JSON.stringify(items) });
  }
};

export const filtersetFiltersFromFilterDTO = (
  filterDTO: ReportFiltersDTO
): { filter_name: string; filter_value: string }[] => {
  const result: { filter_name: string; filter_value: string }[] = [];

  if (filterDTO.districtFilter?.length) {
    push(
      result,
      'district',
      filterDTO.districtFilter.map((d) => ({ v: d.id.toString(), d: d.title }))
    );
  }
  if (filterDTO.schoolYearsFilter?.length) {
    push(
      result,
      'school_year',
      filterDTO.schoolYearsFilter.map((sy) => ({
        v: sy.id.toString(),
        d: sy.name,
      }))
    );
  } else if (filterDTO.schoolYearFilter) {
    push(result, 'school_year', [
      {
        v: filterDTO.schoolYearFilter.id.toString(),
        d: filterDTO.schoolYearFilter.name,
      },
    ]);
  }
  if (filterDTO.schoolFilter?.length) {
    push(
      result,
      'school',
      filterDTO.schoolFilter.map((s) => ({ v: s.id.toString(), d: s.title }))
    );
  }
  if (filterDTO.cohortFilter?.length) {
    push(
      result,
      'cohort',
      filterDTO.cohortFilter.map((c) => ({
        v: c.id.toString(),
        d: (c as DWCohort).name ?? '',
      }))
    );
  }
  if (filterDTO.logTypeFilter?.length) {
    push(
      result,
      'session_type',
      filterDTO.logTypeFilter.map((lt) => ({
        v: lt.id.toString(),
        d: (lt as CoachingSessionTypeDTO).title ?? '',
      }))
    );
  }
  if (filterDTO.assignedCoachFilter) {
    const c = filterDTO.assignedCoachFilter as UserLiteDTO;
    push(result, 'assigned_coach', [{ v: c.id.toString(), d: userName(c) }]);
  }
  if (filterDTO.coacheesFilter?.length) {
    push(
      result,
      'user',
      filterDTO.coacheesFilter.map((c) => ({
        v: c.id.toString(),
        d: userName(c as UserLiteDTO),
      }))
    );
  }
  if (filterDTO.planUserFilter?.length) {
    push(
      result,
      'user',
      filterDTO.planUserFilter.map((u) => ({
        v: u.id.toString(),
        d: userName(u as UserLiteDTO),
      }))
    );
  }
  if (filterDTO.participants?.length) {
    push(
      result,
      'user',
      filterDTO.participants.map((p) => ({
        v: p.id.toString(),
        d: userName(p as UserLiteDTO),
      }))
    );
  }
  if (filterDTO.proFormFilter) {
    const f = filterDTO.proFormFilter as ProFormsAPIResponse;
    push(result, 'form', [{ v: f.id.toString(), d: f.title ?? '' }]);
  }
  if (filterDTO.formAnswersFilter?.length) {
    push(
      result,
      'question_answer_submitted',
      (filterDTO.formAnswersFilter as FormAnswerDTO[]).map((a) => ({
        v: a.id.toString(),
        d: a.title,
      }))
    );
  }
  if (filterDTO.topicFilter) {
    const t = filterDTO.topicFilter as TopicDTO;
    push(result, 'rubric', [{ v: t.id.toString(), d: t.title ?? '' }]);
  }
  if (filterDTO.competencyList?.length) {
    push(
      result,
      'competency',
      (filterDTO.competencyList as CompetencyDTO[]).map((c) => ({
        v: c.id.toString(),
        d: c.title,
      }))
    );
  }
  if (filterDTO.eventFilter?.length) {
    push(
      result,
      'pd_event',
      (filterDTO.eventFilter as PdEvent[]).map((e) => ({
        v: e.id.toString(),
        d: e.title ?? '',
      }))
    );
  }
  if (filterDTO.facilitators?.length) {
    push(
      result,
      'pd_facilitator',
      filterDTO.facilitators.map((f) => ({
        v: f.id.toString(),
        d: userName(f as UserLiteDTO),
      }))
    );
  }
  if (filterDTO.sessionOwners?.length) {
    push(
      result,
      'session_owner',
      filterDTO.sessionOwners.map((o) => ({
        v: o.id.toString(),
        d: userName(o as UserLiteDTO),
      }))
    );
  }
  if (filterDTO.typesFilter?.length) {
    push(
      result,
      'pd_session_type',
      filterDTO.typesFilter.map((t) => ({ v: t, d: t }))
    );
  }
  if (filterDTO.categories?.length) {
    push(
      result,
      'category',
      filterDTO.categories.map((c) => ({ v: c, d: c }))
    );
  }
  if (filterDTO.category_options?.length) {
    push(
      result,
      'category_option',
      filterDTO.category_options.map((o) => ({ v: o, d: o }))
    );
  }
  if (filterDTO.dateRangeFilter) {
    const [from, to] = filterDTO.dateRangeFilter;
    const fmt = (date: NgbDate) =>
      dateTimeFromNgbDate(date).toFormat('yyyy-MM-dd');
    push(result, 'daterange', [
      { v: fmt(from), d: fmt(from) },
      { v: fmt(to), d: fmt(to) },
    ]);
  }
  if (filterDTO.keyword) {
    push(result, 'keyword', [{ v: filterDTO.keyword, d: filterDTO.keyword }]);
  }
  if (filterDTO.virtualFilter !== undefined) {
    push(result, 'is_virtual', [
      {
        v: filterDTO.virtualFilter.toString(),
        d: filterDTO.virtualFilter === 1 ? 'Virtual' : 'On-Site',
      },
    ]);
  }
  if (filterDTO.completedFilter !== undefined) {
    push(result, 'pd_session_completed', [
      {
        v: filterDTO.completedFilter.toString(),
        d: filterDTO.completedFilter === 1 ? 'Completed' : 'Not Completed',
      },
    ]);
  }
  if (filterDTO.hasOption !== undefined) {
    push(result, 'has_option', [
      {
        v: booleanNumberFromBoolean(filterDTO.hasOption).toString(),
        d: filterDTO.hasOption ? 'Yes' : 'No',
      },
    ]);
  }
  if (filterDTO.isReviewed !== undefined) {
    push(result, 'is_reviewed', [
      {
        v: booleanNumberFromBoolean(filterDTO.isReviewed).toString(),
        d: filterDTO.isReviewed ? 'Reviewed' : 'Not Reviewed',
      },
    ]);
  }

  return result;
};
